// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartSelectionStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { computed, observable, action } from 'mobx';
import { isNil, flow, getOr, find, isEmpty } from 'lodash/fp';

import fetcher from 'fetchers/Izenda.ReportsDataFetcher';
import ReportPartSelection from './models/Dashboard/Izenda.ReportPartSelection';
import reportDesignerFetcher from 'fetchers/Izenda.ReportDesignerDataFetcher';
import ReportDesignerConstants from 'logic/data/Izenda.ReportDesignerConstants';
import UiState from 'logic/Izenda.UiState';
import AppStore from 'logic/Izenda.AppStore';

class ReportPartSelectionStore {
  @observable reportPartSelection: any = null;
  @observable onSearchPromise: any = null;
  @observable globalReportName: any = null;
  @observable localReportName: any = null;

  @computed
  get tenantId() {
    return UiState.getTenantId();
  }

  @computed
  get statusSaveInto() {
    return {
      visible: isNil(UiState.tenantId) && !!UiState.currentUser.systemAdmin
    };
  }

  @computed
  get isCategoryDisabled() {
    return flow(getOr(null, 'displayCriteria.isGlobal'), isNil)(this.reportPartSelection);
  }

  constructor() {
    this.excludeIds = [];
    this.reset();
  }

  /**
   * apply new search criteria
   * @method onSearch
   * @return {Function}
   */
  onSearch(excludeIds: Array<string>) {
    this.reportPartSelection.applyDisplayCriteria();
    this.excludeIds = excludeIds;

    return action(() => {
      this.onSearchPromise = this.updateReportGrid();
      return this.onSearchPromise.finally(() => {
        this.onSearchPromise = null;
      });
    })();
  }

  /**
   * Cancel the observer
   */
  destroy() {
    this.reportPartSelection.destroy();
  }

  /**
   * load list of report after sort and paging
   * @method updateReportGrid
   * @return {Promise}     Ajax promise
   */
  updateReportGrid() {
    const reportPartSelection = this.reportPartSelection;
    const query = reportPartSelection.buildSearchReportQuery(this.excludeIds);

    return fetcher.searchReport(query).then(reportPartSelection.readListReport.bind(reportPartSelection));
  }

  /**
   * reset search
   * @method reset
   */
  reset() {
    this.reportPartSelection = new ReportPartSelection();
    this.loadCategorySettings();
    return this.loadCategories();
  }

  /**
   * To get the name text "Global/ Local"
   */
  loadCategorySettings() {
    AppStore.loadCategorySettings().then(
      action(result => {
        const globalReportType = find({ isGlobal: true }, result);
        const localReportType = find({ isGlobal: false }, result);
        this.globalReportName = getOr(null, 'name', globalReportType);
        this.localReportName = getOr(null, 'name', localReportType);
      })
    );
  }

  /**
   * load category
   * @method loadCategories
   * @return {[type]}       [description]
   */
  loadCategories() {
    const reportPartSelection = this.reportPartSelection;
    const loadVisible = true;

    return reportDesignerFetcher
      .loadCategories(
        ReportDesignerConstants.REPORT_TYPE_ENUM.REPORT,
        this.tenantId,
        reportPartSelection.isGlobal,
        loadVisible
      )
      .then(reportPartSelection.readListCategory.bind(reportPartSelection));
  }

  /**
   * set category and load subCategory
   * @method selectCategory
   * @param  {[type]}       categoryId
   * @return {Promise}
   */
  @action
  selectCategory(categoryId: string) {
    const reportPartSelection = this.reportPartSelection;
    const selectedCategory = isEmpty(categoryId) ? null : categoryId;
    const loadVisible = true;

    reportPartSelection.setCriteria('selectedCategory', selectedCategory);
    if (isNil(selectedCategory)) {
      return Promise.resolve();
    }

    return reportDesignerFetcher
      .loadSubCategories(categoryId, loadVisible)
      .then(reportPartSelection.readListSubCategory.bind(reportPartSelection));
  }

  /**
   * set "save into" and load category
   * @method selectSaveInto
   */
  @action
  selectSaveInto(globalCategoryType: string) {
    const reportPartSelection = this.reportPartSelection;
    // Types of 'globalCategoryType' are string and number
    const type = globalCategoryType === '' ? null : globalCategoryType;
    reportPartSelection.setCriteria('isGlobal', type);
    reportPartSelection.applyDisplayCriteria();

    if (type != null) {
      this.loadCategories();
    }
  }
}

export default ReportPartSelectionStore;



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ReportPartSelectionStore.js